import React from 'react'
import PropTypes from 'prop-types'
import {Text, VisuallyHidden} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import messages from './messages'
import {styleForAddToCartModal} from './index.style'

/**
 * Component that displays current price of a product with a11y
 * @param price - price of the product
 * @param as - an HTML tag or component to be rendered as
 * @param isRange - show price as range or not
 * @param currency - currency to show the price in
 * @param labelForA11y - label to be used for a11y
 * @param extraProps - extra props to be passed into Text Component
 * @returns {JSX.Element}
 */
const CurrentPrice = ({
    labelForA11y,
    price,
    as,
    isRange = false,
    isFromAddToCartPopup = false,
    currency,
    ...extraProps
}) => {
    const intl = useIntl()
    const currentPriceText = intl.formatNumber(price, {
        style: 'currency',
        currency
    })
    return (
        <>
            {isRange ? (
                <Text
                    as={as}
                    {...extraProps}
                    aria-live={'polite'}
                    aria-label={intl.formatMessage(messages.ariaLabelCurrentPriceWithRange, {
                        currentPrice: currentPriceText
                    })}
                    sx={isFromAddToCartPopup ? styleForAddToCartModal : {}}
                >
                    {intl.formatMessage(messages.currentPriceWithRange, {
                        currentPrice: currentPriceText
                    })}
                </Text>
            ) : (
                <Text
                    as={as}
                    {...extraProps}
                    aria-label={intl.formatMessage(messages.ariaLabelCurrentPrice, {
                        currentPrice: currentPriceText
                    })}
                    sx={isFromAddToCartPopup ? styleForAddToCartModal : {}}
                >
                    {currentPriceText}
                </Text>
            )}
            {/*For screen reader, we want to make sure the product name is announced before the price to avoid confusion*/}
            <VisuallyHidden aria-live="polite" aria-atomic={true}>
                {labelForA11y}
                {isRange
                    ? intl.formatMessage(messages.ariaLabelCurrentPriceWithRange, {
                          currentPrice: currentPriceText
                      })
                    : intl.formatMessage(messages.ariaLabelCurrentPrice, {
                          currentPrice: currentPriceText
                      })}
            </VisuallyHidden>
        </>
    )
}
CurrentPrice.propTypes = {
    price: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    labelForA11y: PropTypes.string,
    as: PropTypes.string,
    isRange: PropTypes.bool,
    extraProps: PropTypes.object,
    isFromAddToCartPopup: PropTypes.bool
}

export default CurrentPrice
